import { ContentCopy } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect } from 'react';
import * as Yup from 'yup';

import { translationLib } from 'entities/translation';

import { EEntityType, LanguageListItem, TranslationEntityItem, TranslationFields } from 'shared/api';

type Props = {
  isOpen: boolean;
  entityType: EEntityType;
  language: LanguageListItem | null;
  item: TranslationEntityItem | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (fields: TranslationFields) => void;
};

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  descriptions: Yup.string().trim().required('Description is required'),
});

const EmptyHint = () => (
  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
    (empty)
  </Typography>
);

export const TranslationDialog = memo(({ isOpen, entityType, language, item, isSaving, onClose, onSubmit }: Props) => {
  const formik = useFormik<TranslationFields>({
    initialValues: { name: '', descriptions: '' },
    validationSchema,
    enableReinitialize: false,
    onSubmit: (values) => {
      onSubmit({
        name: values.name.trim(),
        descriptions: values.descriptions.trim(),
      });
    },
  });

  useEffect(() => {
    if (isOpen && item) {
      void formik.setValues(item.translation ?? { name: '', descriptions: '' }, false);
    } else if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen, item]);

  if (!item || !language) return null;

  const sourceName = item.source.name;
  const sourceDescriptions = item.source.descriptions;

  // Pre-highlight fields that were missing on open, but only while they are still
  // empty — once the user types something, the hint clears.
  const showMissingHint = (field: 'name' | 'descriptions') =>
    Boolean(item.missingFields?.includes(field)) && formik.values[field].trim() === '';

  const copyToField = (field: 'name' | 'descriptions') => {
    void formik.setFieldValue(field, field === 'name' ? sourceName : sourceDescriptions);
  };

  return (
    <Dialog open={isOpen} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" component="div">
          Translate &laquo;{item.code}&raquo; — {language.nativeName}
        </Typography>
        <Typography variant="caption" component="div" color="text.secondary">
          {translationLib.ENTITY_TYPE_LABEL[entityType]} · ID: {item.entityId}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <form id="translation-dialog-form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Source (EN)
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Tooltip title="Copy to Translation Name">
                    <IconButton size="small" onClick={() => copyToField('name')} disabled={!sourceName}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {sourceName ? <Typography variant="body2">{sourceName}</Typography> : <EmptyHint />}
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    Description
                  </Typography>
                  <Tooltip title="Copy to Translation Description">
                    <IconButton size="small" onClick={() => copyToField('descriptions')} disabled={!sourceDescriptions}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {sourceDescriptions ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                    {sourceDescriptions}
                  </Typography>
                ) : (
                  <EmptyHint />
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Translation ({language.code})
              </Typography>

              <TextField
                fullWidth
                size="small"
                name="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.touched.name && formik.errors.name) || showMissingHint('name')}
                helperText={
                  formik.touched.name && formik.errors.name
                    ? formik.errors.name
                    : showMissingHint('name')
                    ? 'Was missing'
                    : ' '
                }
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                multiline
                minRows={6}
                name="descriptions"
                label="Description"
                value={formik.values.descriptions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.touched.descriptions && formik.errors.descriptions) || showMissingHint('descriptions')
                }
                helperText={
                  formik.touched.descriptions && formik.errors.descriptions
                    ? formik.errors.descriptions
                    : showMissingHint('descriptions')
                    ? 'Was missing'
                    : ' '
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton type="submit" form="translation-dialog-form" variant="contained" loading={isSaving}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

TranslationDialog.displayName = 'TranslationDialog';
